import { createClient, createConfig } from '@hey-api/client-fetch'
import { runtimeConfig } from '@/shared/config'
import { ApiError } from './errors'

// Единая линия HTTP-клиента (Hey API client-fetch). Cookie-auth:
// credentials:'include' гонит httpOnly-cookie сессии в каждый запрос.
// CSRF-токен не добавляем — бэкенд защищается SameSite=Strict, csrf-cookie не выпускает.
// baseUrl пустой: бэкенд монтирует два корня-сиблинга — /api/* (huma) и /auth/*
// (authgate). Префиксы навешивают apiRequest / authRequest.
export const apiClient = createClient(
  createConfig({
    baseUrl: '',
    credentials: 'include',
  }),
)

const API_PREFIX = runtimeConfig.API_BASE_URL
const AUTH_PREFIX = '/auth'

// Конец сессии (продление уже отказало) обрабатывается сверху: app инжектит
// сброс session-кэша + увод на страницу входа хаба. shared не может
// импортировать app (FSD), поэтому — через сеттер.
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

// Контуров сессии два (00-architecture § 3), и различить их из JS нечем: обе
// cookie httpOnly. Поэтому фронт помнит последний контур, увиденный в /auth/me,
// и продлевает им. Это подсказка, а не идентификация — ни логина, ни прав в ней
// нет, только куда стучаться на 401.
export type SessionContour = 'employee' | 'customer'

const CONTOUR_KEY = 'stit.deal.session-contour'

function readContour(): SessionContour | null {
  try {
    const raw = localStorage.getItem(CONTOUR_KEY)
    return raw === 'employee' || raw === 'customer' ? raw : null
  } catch {
    // Приватный режим/заблокированное хранилище — работаем без подсказки.
    return null
  }
}

let contour: SessionContour | null = readContour()

export function rememberSessionContour(next: SessionContour): void {
  contour = next
  try {
    localStorage.setItem(CONTOUR_KEY, next)
  } catch {
    // см. readContour
  }
}

export function forgetSessionContour(): void {
  contour = null
  try {
    localStorage.removeItem(CONTOUR_KEY)
  } catch {
    // см. readContour
  }
}

// Обе сессии держат access-cookie короткой (~15 мин), а живость — в
// refresh-cookie: сотрудник на id.stit.tech (Path=/auth), заказчик на
// api.hub.stit.tech (Path=/auth/customer). Ни срока, ни наличия refresh из JS не
// видно — отличить «протухла минуту назад» от «сессии нет» можно только сходив
// в refresh-ручку. Ходим мимо apiClient: 401 самого refresh не должен заходить
// на второй круг.
function postRefresh(url: string): Promise<boolean> {
  return fetch(url, { method: 'POST', credentials: 'include' })
    .then((res) => res.ok)
    .catch(() => false)
}

function refreshOnce(line: SessionContour): Promise<boolean> {
  return postRefresh(
    line === 'customer'
      ? `${runtimeConfig.HUB_API_BASE_URL}/auth/customer/refresh`
      : `${runtimeConfig.ID_BASE_URL}/auth/refresh`,
  )
}

// Порядок попыток решает последний известный контур; на холодном старте (или
// когда подсказка соврала) пробуем оба — сессия есть ровно в одном, и только
// отказ обоих означает «идти на вход». Employee первым: он и частотнее, и
// главнее при обеих cookie (приоритет из hubkit/sso).
async function refreshAnyContour(): Promise<boolean> {
  const order: SessionContour[] =
    contour === 'customer' ? ['customer', 'employee'] : ['employee', 'customer']
  for (const line of order) {
    if (await refreshOnce(line)) {
      rememberSessionContour(line)
      return true
    }
  }
  return false
}

let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  // Refresh ротирующий: параллельные попытки погасили бы друг друга и выбросили
  // живого пользователя, поэтому все 401, пришедшие одновременно, ждут один и
  // тот же промис.
  refreshInFlight ??= refreshAnyContour().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// Выход заказчика гасит сессию там, где она выпущена, — в хабе (deal своей
// cookie заказчику больше не выдаёт). Мимо apiClient: чужой origin и свой корень.
export function logoutCustomerSession(): Promise<void> {
  return fetch(`${runtimeConfig.HUB_API_BASE_URL}/auth/customer/logout`, {
    method: 'POST',
    credentials: 'include',
  })
    .then(() => undefined)
    .catch(() => undefined)
}

// Зона устройства сотрудника уходит в хаб, потому что там она нужна тем местам,
// где экрана нет: текст письма и телеграм-алерта хаб рендерит на сервере и зону
// берёт из своей проекции сотрудника. На экране зона в этой записи не нуждается —
// момент показывается в зоне устройства напрямую (shared/lib/datetime.ts).
// Ручка идемпотентна: тем же значением хаб не пишет вовсе, поэтому сверять с
// сохранённым незачем.
//
// Мимо apiClient: чужой origin, свой корень и никакого 401-повтора — фоновая
// запись не должна ни продлевать сессию, ни ронять экран. Отказ глотается,
// следующая загрузка догонит.
export function reportDeviceTimezone(timezone: string): Promise<void> {
  return fetch(`${runtimeConfig.ID_BASE_URL}/api/employees/me/timezone`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timezone }),
  })
    .then(() => undefined)
    .catch(() => undefined)
}

interface RequestOpts {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string
  path?: Record<string, unknown>
  query?: Record<string, unknown>
  body?: Record<string, unknown> | unknown[]
  headers?: Record<string, string>
}

async function request<T>(prefix: string, opts: RequestOpts): Promise<T> {
  const call = { ...opts, url: `${prefix}${opts.url}` }
  let result = await apiClient.request<T, unknown>(call)
  // Одна попытка: refresh либо продлевает сессию (тогда повтор проходит), либо
  // она кончилась и второй заход даст тот же 401.
  if (result.response.status === 401 && (await refreshSession())) {
    result = await apiClient.request<T, unknown>(call)
  }
  const { data, error, response } = result
  if (response.status === 401) onUnauthorized?.()
  if (!response.ok || error !== undefined) {
    throw ApiError.from(response, error)
  }
  return data as T
}

// Запрос к /api/* (huma). entities/<slice>/api оборачивают это в
// queryOptions/мутации вручную.
export function apiRequest<T>(opts: RequestOpts): Promise<T> {
  return request<T>(API_PREFIX, opts)
}

// Запрос к /auth/* (authgate deal): вход и выход живут в хабе, у deal остаётся
// /auth/me — identity смотрящего из claims его сессии.
export function authRequest<T>(opts: RequestOpts): Promise<T> {
  return request<T>(AUTH_PREFIX, opts)
}
